import React from 'react';
import { AdminPanelProps } from '../../types/game.types';
import { useAppContext } from '../../hooks/useAppContext';
import { Modal, Button, ActionBar, StatusBadge, SectionCard } from '../common';
import { PlayerAvatar } from '../common/PlayerAvatar';
import { IslandMembersPanel } from '../room/IslandMembersPanel';
import { getStatusLabel, toUserStatus } from '../../utils/statusColors';
import { FaUsers } from 'react-icons/fa';

const AdminPanel: React.FC<AdminPanelProps> = ({ isOpen, onClose }) => {
  const { auth, roomConnection } = useAppContext();
  
  // Get current room and player data from roomConnection
  const room = roomConnection.currentRoom;
  const currentPlayerCount = roomConnection.playersData?.length || 0;
  
  // Status colors and labels now centralized in utils/statusColors.ts
  //
  // This panel shows who is in the island and nothing else. The home-zone
  // chips and the per-player unclaim controls that used to live here read a
  // `connectionState` property the connection hook never exposed and filtered
  // on a zone status the server never sends, so they rendered for nobody;
  // they were removed rather than repaired. `roomAPI.adminUnclaimZone` and its
  // endpoint (`DELETE /rooms/:id/zones/:zoneId/claim/:userId`) are untouched,
  // and now have no UI at all.

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" title="Island Administration">
      <div className="space-y-6">
        
        {/* Room Info Card */}
        <SectionCard title="Island Information" icon={FaUsers} className="mb-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className="text-gray-400 text-sm block mb-1">Island Name</span>
              <span className="text-white font-medium text-lg">{room?.name || 'Unknown'}</span>
            </div>
            <div>
              <span className="text-gray-400 text-sm block mb-1">Connected Players</span>
              <span className="text-white font-medium text-lg">
                {currentPlayerCount} / {room?.maxPlayers || '∞'}
              </span>
            </div>
          </div>
        </SectionCard>

        {/* Membership and invitations — the shared panel of §8, mounted here
            in admin mode and in the platform console in superadmin mode. It
            lists who holds a role on this island, online or not; the live
            session list below is a different thing entirely. */}
        {room?.id ? (
          <IslandMembersPanel roomId={room.id} mode="admin" />
        ) : (
          <p className="text-gray-400 italic text-center py-5">No island selected</p>
        )}

        {/* Connected players: live sessions and their home zones. Roles and
            removal live in the member list above. */}
        <SectionCard title="Connected Players" icon={FaUsers} className="mb-6">
          
          {roomConnection.playersData && roomConnection.playersData.length > 0 ? (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {roomConnection.playersData.map((player) => {
                const isCurrentUser = player.userId === auth.user?.id;
                
                return (
                  <div 
                    key={player.userId} 
                    className={`flex items-center justify-between p-3 rounded-lg transition-all duration-200 ${
                      isCurrentUser 
                        ? 'bg-game-golden/10 border border-game-golden/30' 
                        : 'bg-game-dark/30 border border-game-medium/20 hover:bg-game-dark/50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {/* Player Avatar */}
                      <PlayerAvatar 
                        username={player.username} 
                        avatar={player.avatar} 
                        size="sm" 
                      />
                      
                      {/* Status Indicator */}
                      <StatusBadge 
                        type="status" 
                        value={toUserStatus(player.status)} 
                        size="sm"
                        showLabel={false}
                      />
                      
                      {/* Player Info */}
                      <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                          <span className="text-white font-medium">
                            {player.username}
                            {isCurrentUser && <span className="text-game-golden ml-1">(You)</span>}
                          </span>
                        </div>
                        <span className="text-gray-400 text-xs">
                          {getStatusLabel(player.status || 'available')}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-gray-400 italic text-center py-5">
              No players in the island
            </p>
          )}
        </SectionCard>

        {/* Actions Footer */}
        <ActionBar align="right" className="pt-4 border-t border-game-medium/30">
          <Button
            variant="secondary"
            onClick={onClose}
            className="px-6"
          >
            Close
          </Button>
        </ActionBar>
      </div>
    </Modal>
  );
};

export default AdminPanel;